import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, ArrowRight, FolderKanban, MessageSquareWarning } from "lucide-react";

const ICONS = {
  reclamation: MessageSquareWarning,
  dossier: FolderKanban,
};

const LABELS = {
  reclamation: "Réclamation",
  dossier: "Dossier",
  ticket: "Ticket",
};

const LINKS = {
  reclamation: "/reclamations",
  dossier: "/dossiers",
  ticket: "/file-attente",
};

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

export default function Discussions() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/messages/recent", { params: { limit: 30 } });
      setRows(data);
    } catch (err) {
      console.warn("[Discussions] load recent failed", err?.response?.status || err?.message);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Collaboration"
        title="Discussions équipe"
        description="Vos échanges récents sur les dossiers et réclamations — messages, mentions et notifications."
        testid="discussions-header"
      />

      {rows.length === 0 ? (
        <Card className="border border-slate-200 bg-white">
          <CardContent className="p-16 text-center text-slate-400">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <div className="text-lg text-slate-500">Aucune discussion pour le moment</div>
            <div className="text-sm mt-2">Ouvrez un dossier ou une réclamation pour démarrer un échange avec votre équipe.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((c) => {
            const Icon = ICONS[c.entity_type] || MessageSquare;
            return (
              <button
                key={`${c.entity_type}:${c.entity_id}`}
                onClick={() => nav(LINKS[c.entity_type] || "/")}
                className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:border-blue-800 card-hover"
                data-testid={`discussion-${c.entity_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 grid place-items-center">
                      <Icon className="w-4 h-4 text-blue-800" />
                    </div>
                    <Badge variant="outline" className="text-slate-600 capitalize">{LABELS[c.entity_type] || c.entity_type}</Badge>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{c.count} msg</Badge>
                </div>
                <div className="font-display text-lg font-semibold text-slate-900 leading-snug line-clamp-1">{c.title}</div>
                <div className="text-sm text-slate-600 mt-1 line-clamp-2">
                  <span className="font-medium text-slate-800">{c.last_by} :</span> {c.last_content}
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span className="font-mono">{timeAgo(c.last_at)}</span>
                  <span className="inline-flex items-center gap-1 text-blue-800 font-medium">Ouvrir <ArrowRight className="w-3 h-3" /></span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
